import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Loader, Message, Segment } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import { partyIsClient } from "../../../../app/common/util/util";
import {
  functionGetPropertyIntelligence,
  functionResumePropertyIntelligence,
  functionSavePropertyIntelligenceClientSummary,
  functionUpdatePropertyIntelligenceReport,
} from "../../../../app/firestore/functionsService";
import { isResearchFinished, isResearchInProgress } from "./piModel";
import PiResearchProgress from "./PiResearchProgress";
import PiReportView from "./PiReportView";
import PiShareModal from "./PiShareModal";


const POLL_MS = 5000;


/**
 * The Property Intelligence report. Loads the SAVED report (never
 * re-researches), polls while research is running so a reload or a second
 * browser reconnects to the same job, and offers editing, the client
 * summary and sharing once complete. There is deliberately no "regenerate".
 */
export default function PropertyIntelligenceModal({ transaction, propertyKey: requestedPropertyKey, initialTab = "overview", onChanged }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  // Below this width the eight tabs become a select menu instead of a
  // horizontally scrolling strip.
  const isNarrow = useMediaQuery({ query: "(max-width:900px)" });
  const bodyRef = useRef(null);
  // false = compact progress view; true = full report layout. A report that is
  // already finished when the modal opens goes straight to the report.
  const [reportOpen, setReportOpen] = useState(false);
  const { allParties } = useSelector((state) => state.transaction);
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTabState] = useState(initialTab);
  const setTab = useCallback((next) => {
    setTabState(next);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);
  const [resuming, setResuming] = useState(false);
  const [share, setShare] = useState(null); // {recipientIds}
  const [pollKey, setPollKey] = useState(0); // bumped to restart polling after Resume
  const pollRef = useRef(null);
  const lastStatusRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    try {
      const data = await functionGetPropertyIntelligence({ transactionId: transaction.id, propertyKey: requestedPropertyKey });
      setView(data);
      setError(null);
      const status = data?.report?.status;
      if (lastStatusRef.current === null && isResearchFinished(status)) setReportOpen(true);
      if (lastStatusRef.current && lastStatusRef.current !== status && onChangedRef.current) onChangedRef.current(status);
      lastStatusRef.current = status;
      return data;
    } catch (e) {
      setError(e?.message || "Unable to load Property Intelligence.");
      return null;
    }
  }, [transaction.id, requestedPropertyKey]);

  // Poll only while research is running; reopening the modal (or a second
  // browser) simply reconnects to the same saved job.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      const data = await load();
      if (cancelled) return;
      if (data?.report && isResearchInProgress(data.report.status)) pollRef.current = setTimeout(tick, POLL_MS);
    }
    tick();
    return () => {
      cancelled = true;
      clearTimeout(pollRef.current);
    };
  }, [load, pollKey]);

  const report = view?.report;
  const propertyKey = view?.propertyKey;
  const clientParties = (allParties || []).filter((p) => partyIsClient(transaction, p) && p.email);

  async function handleEdit(edit) {
    try {
      const res = await functionUpdatePropertyIntelligenceReport({ transactionId: transaction.id, propertyKey, edit });
      // The server returns the refreshed reading model with the edit; the
      // diagnostics (which need module state) are kept from the last load.
      setView((v) => ({
        ...v,
        report: { ...v.report, agentEdits: res.agentEdits },
        presentation: res.presentation ? { ...res.presentation, diagnostics: v.presentation?.diagnostics || null } : v.presentation,
      }));
    } catch (e) {
      toast.error(e?.message || "Unable to save your change.");
      throw e;
    }
  }

  async function handleSaveSummary({ draft, approve }) {
    const res = await functionSavePropertyIntelligenceClientSummary({ transactionId: transaction.id, propertyKey, draft, approve });
    setView((v) => ({ ...v, report: { ...v.report, clientSummary: res.clientSummary } }));
  }

  async function handleResume() {
    setResuming(true);
    try {
      await functionResumePropertyIntelligence({ transactionId: transaction.id, propertyKey });
      toast.success("Research resumed.");
      setPollKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.message || "Unable to resume research.");
    } finally {
      setResuming(false);
    }
  }

  // Closing never touches the research job - it keeps running server-side.
  function handleClose() {
    dispatch(closeModal({ modalType: "PropertyIntelligenceModal" }));
  }

  if (!reportOpen || !(report && isResearchFinished(report.status))) {
    return (
      <ModalWrapper size="small" style={{ width: "min(640px, calc(100vw - 32px))", margin: "0 auto" }}>
        {/* Same frame as the app's other modals: white Segment with the gray outline. */}
        <Segment clearing>
        {!view && !error && <Loader active inline="centered" content="Loading Property Intelligence..." style={{ margin: "40px auto" }} />}
        {error && <Message error content={error} />}
        {view && !report && <Message info content="No Property Intelligence research exists for this property yet." />}
        {report && (
          <PiResearchProgress
            transaction={transaction}
            report={report}
            modules={view.modules}
            resuming={resuming}
            onResume={handleResume}
            onViewReport={() => setReportOpen(true)}
            onClose={handleClose}
            isMobile={isMobile}
          />
        )}
        </Segment>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper size="fullscreen" className="pi-report-modal">
      <PiReportView
        transaction={transaction}
        report={report}
        presentation={view?.presentation}
        modules={view.modules}
        tab={tab}
        onTab={setTab}
        isNarrow={isNarrow}
        bodyRef={bodyRef}
        resuming={resuming}
        clientParties={clientParties}
        onClose={handleClose}
        onResume={handleResume}
        onEdit={handleEdit}
        onSaveSummary={handleSaveSummary}
        onShare={(recipientIds) => setShare({ recipientIds })}
      />

      {share && report && (
        <PiShareModal
          open
          onClose={() => setShare(null)}
          report={report}
          propertyKey={propertyKey}
          transaction={transaction}
          clientParties={clientParties}
          initialRecipientIds={share.recipientIds}
        />
      )}
    </ModalWrapper>
  );
}
